import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import {
    COGNITO_SSO_POPUP_MSG,
    type CognitoSsoPopupMessage,
} from '../utils/cognitoSsoPopup';
import AuthService from '../services/auth.service';
import { getAilaunchRedirectUrl } from '../utils/redirect.util';
// import { CleverTapService } from '../utils/clevertap.utils';

const TRY_AI_FLOW = 'try-ai-flow';
const FREE_TRIAL_PLAN = 'free-trial-plan';
const UTM_STORAGE_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'ad_id',
    'fbc_id',
    'h_ad_id',
    'utm_id',
] as const;

type SsoStateData = {
    planId?: string;
    provider?: string;
    flow?: string;
    utm_params?: Record<string, string>;
};

const parseJwtPayload = <T,>(token?: string): T | null => {
    if (!token) return null;
    try {
        const payloadPart = token.split('.')[1];
        if (!payloadPart) return null;

        const normalizedBase64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
        const paddedBase64 = normalizedBase64 + '='.repeat((4 - (normalizedBase64.length % 4)) % 4);
        const jsonPayload = window.atob(paddedBase64);
        return JSON.parse(jsonPayload) as T;
    } catch (error) {
        console.error('[SSOCallback] Failed to parse token payload', error);
        return null;
    }
};

const getEmailFromIdToken = (idToken?: string): string => {
    const payload = parseJwtPayload<{ email?: string }>(idToken);
    return payload?.email ?? '';
};

const getCompanyIdFromMetadataToken = (metadataToken?: string): string => {
    const payload = parseJwtPayload<{
        companyId?: string;
        companies?: Array<{ id?: string }>;
    }>(metadataToken);

    const companyId = payload?.companies?.[0]?.id || payload?.companyId || '';

    if (!companyId) {
        console.info('[SSOCallback] companyId not found in metadata token payload');
        return '';
    }

    return companyId;
};

const SSOCallback = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const oauthError = searchParams.get('error');
        const oauthErrorDescription = searchParams.get('error_description');

        // Cognito Hosted UI opened in a popup: forward code to opener and close (tokens must be stored on opener).
        if (window.opener && typeof window.opener.postMessage === 'function') {
            const targetOrigin = window.location.origin;
            let payload: CognitoSsoPopupMessage;
            if (oauthError) {
                payload = {
                    type: COGNITO_SSO_POPUP_MSG,
                    error: oauthError,
                    error_description: oauthErrorDescription || undefined,
                };
            } else if (code) {
                payload = {
                    type: COGNITO_SSO_POPUP_MSG,
                    code,
                    state: state ?? '',
                };
            } else {
                payload = {
                    type: COGNITO_SSO_POPUP_MSG,
                    error: 'no_code',
                    error_description: 'No authorization code received',
                };
            }
            window.opener.postMessage(payload, targetOrigin);
            window.close();
            return;
        }

        const handleSsoCallback = async () => {
            try {
                if (!code) {
                    throw new Error('No authorization code received');
                }

                // Parse state to get planId and register flag if they exist
                let planId = localStorage.getItem('planId') || FREE_TRIAL_PLAN;
                let utmParams: string | undefined = undefined;
                let provider = 'Unknown';
                let isRegistration = false;
                
                let stateData: SsoStateData | undefined;
                if (state) {
                    try {
                        stateData = JSON.parse(state) as SsoStateData;
                        console.info('[SSOCallback] Parsed state payload', stateData);
                        if (stateData.planId) {
                            planId = stateData.planId;
                        }
                        if (stateData.utm_params && Object.keys(stateData.utm_params).length > 0) {
                            utmParams = JSON.stringify(stateData.utm_params);
                        }
                        // Extract provider from state if available
                        if (stateData.provider) {
                            provider = stateData.provider;
                        }
                    } catch (e) {
                        console.error('[SSOCallback] Failed to parse state payload', e);
                    }
                }

                // Track SSO callback initiated
                // const ssoCallbackData = {
                //     'Provider': provider,
                //     'Plan ID': planId,
                //     'Page Title': document.title,
                //     'Page URL': window.location.href,
                //     'Timestamp': new Date().toISOString(),
                // };
                // CleverTapService.trackEvent('SSO Callback Initiated', ssoCallbackData);

                const isAiFlowTrialPlan = stateData?.flow === TRY_AI_FLOW;
                const isTrialPlan = planId === FREE_TRIAL_PLAN;

                // Exchange code for user info (registration vs login is determined on the API)
                const response = await AuthService.getTokensFromCode(
                    code,
                    isTrialPlan,
                    utmParams,
                    isAiFlowTrialPlan,
                );

                console.log('response', response);

                // Clear UTM params from localStorage
                UTM_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
                
                if (response.data.linkAccounts) {
                    // Track account linking required
                    // const linkAccountData = {
                    //     'Provider': provider,
                    //     'Page Title': document.title,
                    //     'Page URL': window.location.href,
                    //     'Timestamp': new Date().toISOString(),
                    // };
                    // CleverTapService.trackEvent('Account Linking Required', linkAccountData);
                    
                    navigate('/link-account');
                    return;
                }

                isRegistration = response.data.isRegistration;
                
                // Track successful SSO login
                // const successData = {
                //     'Provider': provider,
                //     'User Email': idToken?.email || '',
                //     'User Name': idToken?.name || '',
                //     'Page Title': document.title,
                //     'Page URL': window.location.href,
                //     'Timestamp': new Date().toISOString(),
                // };
                // CleverTapService.trackEvent('SSO Login Successful', successData);

                if (isAiFlowTrialPlan) {
                    const base = getAilaunchRedirectUrl();
                    window.location.href = isRegistration ? base : `${base}&isLogin=true`;
                    return;
                }
                
                if (isRegistration) {
                    console.info('[SSOCallback] Registration SSO completed', { provider, planId });
                    const ewEmail = getEmailFromIdToken(response.data.idToken);
                    const companyId = getCompanyIdFromMetadataToken(response.data.metadataToken);
                    const loadingDashboardParams = new URLSearchParams({
                        ew_email: ewEmail,
                        planId: planId || '',
                        companyId,
                        showOnboarding: 'true',
                        price: '0',
                        currency: 'USD',
                        coupon: '',
                        plan_name: planId || '',
                        customer_type: 'with_cc',
                        sso: 'true',
                    });
                    navigate(`/loading-dashboard?${loadingDashboardParams.toString()}`);
                } else {
                    console.info('[SSOCallback] Login SSO completed', { provider });
                    navigate('/dashboard/overview?redirect=easywebinar');
                }
            } catch (error) {
                console.error('[SSOCallback] SSO callback flow failed', error);
                // Track SSO callback error
                // const errorData = {
                //     'Error Type': error instanceof Error ? error.message : 'Unknown Error',
                //     'Page Title': document.title,
                //     'Page URL': window.location.href,
                //     'Timestamp': new Date().toISOString(),
                // };
                // CleverTapService.trackError('SSO Callback Error', error instanceof Error ? error.message : 'Unknown Error', {}, errorData);
                
                navigate('/signup');
            }
        };

        handleSsoCallback();
    }, [navigate, searchParams]);

    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
        </Box>
    );
};

export default SSOCallback;